import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, List, Moon, Sun, X } from '@phosphor-icons/react';
import { NAV_LINKS } from './landingContent';
import { usePrefersReducedMotion } from './useLandingReveal';

export function scrollToSection(hash, reducedMotion) {
  const target = document.querySelector(hash);
  if (!target) return;
  target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
}

function LandingNavigation({
  logoSrc,
  iconSrc,
  isDark,
  onToggleTheme,
  onSignIn,
  onCreateVideo,
  sentinelRef,
}) {
  const [solid, setSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const overlayRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const sentinel = sentinelRef?.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setSolid(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const menuButton = menuButtonRef.current;
    const overlay = overlayRef.current;
    const focusables = overlay
      ? Array.from(overlay.querySelectorAll('a[href], button:not([disabled])'))
      : [];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab' || focusables.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (first) first.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      menuButton?.focus();
    };
  }, [closeMenu, menuOpen]);

  const handleAnchorClick = (event, href) => {
    if (!href.startsWith('#')) return;
    event.preventDefault();
    closeMenu();
    scrollToSection(href, reducedMotion);
  };

  const handleThemeToggle = () => {
    onToggleTheme(isDark ? 'light' : 'dark');
  };

  const handleCreateVideo = () => {
    closeMenu();
    onCreateVideo('nav');
  };

  const renderLink = (link, className, activeClass) => {
    const isAnchor = link.href.startsWith('#');
    const classes = `${className}${link.id === 'product' && activeClass ? ` ${activeClass}` : ''}`;
    if (isAnchor) {
      return (
        <a
          key={link.id}
          href={link.href}
          className={classes}
          onClick={(event) => handleAnchorClick(event, link.href)}
        >
          {link.label}
        </a>
      );
    }
    return (
      <Link key={link.id} to={link.href} className={classes} onClick={closeMenu}>
        {link.label}
      </Link>
    );
  };

  return (
    <>
      <header className={`lp-nav${solid ? ' lp-nav--scrolled' : ''}`}>
        {/* Full wordmark over the transparent bar; it swaps to the icon once
            the bar collapses into its scrolled card. */}
        <Link to="/" className="lp-nav__logo lp-focusable" aria-label="Latted home">
          <img
            className="lp-nav__logo-img lp-nav__logo-img--wordmark"
            src={logoSrc}
            alt="Latted"
          />
          <img
            className="lp-nav__logo-img lp-nav__logo-img--icon"
            src={iconSrc || logoSrc}
            alt=""
            aria-hidden="true"
          />
        </Link>

        <nav className="lp-nav__links" aria-label="Primary">
          {NAV_LINKS.map((link) => renderLink(link, 'lp-nav__link lp-focusable', 'lp-nav__link--active'))}
        </nav>

        <div className="lp-nav__actions">
          <button
            type="button"
            className="lp-icon-btn lp-focusable"
            onClick={handleThemeToggle}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button type="button" className="lp-nav__signin lp-focusable" onClick={onSignIn}>
            Sign in
          </button>
          <button
            type="button"
            className="lp-btn-primary lp-nav__cta lp-focusable"
            onClick={handleCreateVideo}
          >
            Create a video
            <ArrowRight size={14} weight="bold" aria-hidden="true" />
          </button>
          <button
            ref={menuButtonRef}
            type="button"
            className={`lp-icon-btn lp-nav__menu-btn lp-focusable${menuOpen ? ' lp-nav__menu-btn--open' : ''}`}
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="lp-menu-overlay"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="lp-nav__menu-icon lp-nav__menu-icon--bars" aria-hidden="true">
              <List size={18} />
            </span>
            <span className="lp-nav__menu-icon lp-nav__menu-icon--close" aria-hidden="true">
              <X size={18} />
            </span>
          </button>
        </div>
      </header>

      <div
        id="lp-menu-overlay"
        ref={overlayRef}
        className={`lp-menu-overlay${menuOpen ? ' lp-menu-overlay--open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav aria-label="Mobile">
          <ul className="lp-menu-overlay__links">
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                {renderLink(link, 'lp-menu-overlay__link lp-focusable', null)}
              </li>
            ))}
          </ul>
        </nav>
        <div className="lp-menu-overlay__footer">
          <button type="button" className="lp-menu-overlay__signin lp-focusable" onClick={() => { closeMenu(); onSignIn(); }}>
            Sign in
          </button>
          <button
            type="button"
            className="lp-btn-primary lp-menu-overlay__cta lp-focusable"
            onClick={handleCreateVideo}
          >
            Create a video
            <ArrowRight size={16} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}

export default LandingNavigation;
